/* eslint-disable no-fallthrough */
import React, { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AccessibilityInfo, StyleSheet, TVEventHandler, View } from 'react-native';
import PropTypes from 'prop-types';

import { Theme } from '@constants';

import { VideoControls } from './Controls';

import { isAndroid, isWeb } from '../../../helpers/Platform';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCAccessibleContent } from '../../focusable/TWCAccessibleContent/TWCAccessibleContent';
import { TWCText } from '../../primitives/TWCText/TWCText';

// Constants
const CONSTANTS = {
  liveIndicatorDotSize: Theme.px(12),
  progressIndicatorWidth: Theme.px(6),
  progressIndicatorHeight: Theme.px(30),
  progressTimeWidth: Theme.px(200),
  SEEK_AMOUNT_SEC: 15,
};

// Util functions
const makeInRange = (value, minValue, maxValue) => Math.min(Math.max(value, minValue), maxValue);

// Progress indicator
const Progress = ({ progress, width, height, duration, focused }) => {
  const leftStyle = focused ? styles.progressLeftFocus : styles.progressLeft;
  const indicatorProgressOffset = focused ? CONSTANTS.progressIndicatorWidth / width / 2 : 0;
  const progressTimeString = TimeUtils.secondsToDuration(Math.floor(duration * progress));

  return (
    <>
      <View style={[styles.progress, { width }]}>
        <View style={[leftStyle, { flex: Math.max(progress - indicatorProgressOffset, 0), height }]} />
        {focused && <View style={[styles.progressIndicator, { flex: indicatorProgressOffset * 2 }]} />}
        <View
          style={[
            styles.progressRight,
            {
              flex: Math.max(1 - progress - indicatorProgressOffset, 0),
              height,
            },
          ]}
        />
      </View>
      {focused && (
        <TWCText
          noTestID
          importantForAccessibility={'no'}
          style={[styles.focusTimeString, { left: width * progress - CONSTANTS.progressTimeWidth / 2 }]}
          variant={'mediumText30'}
        >
          {progressTimeString}
        </TWCText>
      )}
    </>
  );
};

// Live text/dot indicator
const LiveIndicator = () => (
  <View style={styles.wrapper}>
    <View style={styles.liveDot} />
    <TWCText importantForAccessibility={'no'} testID={'live_indicator_label'} variant={'mediumText30'}>
      {'LIVE'}
    </TWCText>
  </View>
);

/**
 * Indicator for video controls to show progress though the video.
 * @param {*} param0 Pass in progress, width, height, and isLive.
 * Progress should be a number from 0-1 indicating percent complete for the video.
 * It is clamped to that range (so values < 0 will give 0 and > 1 will give 1).
 * If there is no good data for this (live EPC call fails), will hide the progress
 * indicator. Width and height apply to the progress indicator.
 *
 * isLive is a boolean. If true will display LIVE text and dot next to progress.
 * @returns
 */
export const VideoProgressIndicator = forwardRef(
  (
    {
      progress,
      width,
      height,
      duration,
      isLive,
      seekValue,
      onControl,
      showControls,
      nextFocusLeft,
      nextFocusRight,
      nextFocusUp,
      nextFocusDown,
    },
    reference,
  ) => {
    const hasData = progress !== null && progress !== undefined;

    const [focused, setFocused] = useState(false);
    const progressValue = seekValue || seekValue === 0 ? seekValue / duration : progress;

    const controlHandler = keyEvent => {
      let key = keyEvent.key;

      if (!focused && (key === 'ArrowLeft' || key === 'ArrowRight')) {
        key = '';
      }

      const keycode = keyEvent?.keyCode ?? 0;
      const isBackPress = key === 'Back' || key === 'Escape' || keycode === 10009 || keycode === 8;

      if (!isBackPress) {
        showControls?.();
      }

      switch (key) {
        case 'rewind':
          showControls && showControls();
        case 'ArrowLeft': {
          if (progressValue > 0) {
            onControl && onControl(VideoControls.SEEKING, progressValue * duration - CONSTANTS.SEEK_AMOUNT_SEC);
          }
          break;
        }
        case 'fastForward':
          showControls && showControls();
        case 'ArrowRight': {
          if (progressValue < 1) {
            onControl && onControl(VideoControls.SEEKING, progressValue * duration + CONSTANTS.SEEK_AMOUNT_SEC);
          }
          break;
        }
      }
    };

    useEffect(() => {
      const tvEventHandler = new TVEventHandler();

      if (isWeb) {
        document.addEventListener('keydown', controlHandler, true);
      } else {
        const handleEvent = (cmp, event) => {
          if (event && event.eventType && event.eventKeyAction === 0) {
            if (focused && (event.eventType === 'left' || event.eventType === 'right')) {
              controlHandler({
                key: event.eventType === 'left' ? 'ArrowLeft' : 'ArrowRight',
              });
            } else {
              controlHandler({
                key: event.eventType,
              });
            }
          }
        };

        tvEventHandler.enable(null, handleEvent);
      }

      return () => {
        if (isWeb) {
          document.removeEventListener('keydown', controlHandler, true);
        } else {
          tvEventHandler?.disable();
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [progress, seekValue, duration, focused]);

    const tPath = 'videoControls';
    const { t } = useTranslation('translation', { keyPrefix: tPath });

    const progressTimeString = TimeUtils.secondsToDuration(Math.floor(duration * progressValue));
    const [progressSec, progressMin] = progressTimeString.split(':').reverse();
    const endTimeString = TimeUtils.secondsToDuration(Math.floor(duration));
    const [endSec, endMin] = endTimeString.split(':').reverse();

    const progressAccessibleTime = t('timeAccessible', {
      seconds: progressSec,
      minutes: progressMin,
    });
    const endAccessibleTime = t('timeAccessible', {
      seconds: endSec,
      minutes: endMin,
    });

    return (
      <TWCAccessibleContent
        key={'progress_indicator'}
        accessibilityActions={[
          { name: 'increment', label: 'seek forward' },
          { name: 'decrement', label: 'seek backward' },
          { name: 'longpress', label: 'seek' },
        ]}
        accessibilityHint={
          isAndroid
            ? t('androidScrubberAccessibilityHint')
            : `${t('webScrubberAccessibilityHint')} ${t('scrubberAccessibleValue', {
                time: progressAccessibleTime,
                end: endAccessibleTime,
              })}`
        }
        accessibilityLabel={t('scrubberAccessibilityLabel')}
        accessibilityRole={'adjustable'}
        accessibilityValue={{
          text: t('scrubberAccessibleValue', {
            time: progressAccessibleTime,
            end: endAccessibleTime,
          }),
        }}
        nextFocusDown={nextFocusDown}
        nextFocusLeft={nextFocusLeft}
        nextFocusRight={nextFocusRight}
        nextFocusUp={nextFocusUp}
        ref={reference}
        shouldUseContextReference={false}
        testID={'progress_indicator_section'}
        onAccessibilityAction={event => {
          console.log(event.nativeEvent);
          switch (event.nativeEvent.actionName) {
            case 'increment':
              if (progressValue >= 0) {
                onControl?.(VideoControls.SEEKING, progressValue * duration + CONSTANTS.SEEK_AMOUNT_SEC);
                const newStringValue = TimeUtils.secondsToDuration(
                  makeInRange(Math.floor(duration * progressValue + CONSTANTS.SEEK_AMOUNT_SEC), 0, duration),
                );
                const [newSec, newMin] = newStringValue.split(':').reverse();

                AccessibilityInfo.announceForAccessibility(
                  t('seekToAnnouncement', { seconds: newSec, minutes: newMin }),
                );
              }
              break;
            case 'decrement':
              if (progressValue >= 0) {
                onControl?.(VideoControls.SEEKING, progressValue * duration - CONSTANTS.SEEK_AMOUNT_SEC);
                const newStringValue = TimeUtils.secondsToDuration(
                  makeInRange(Math.floor(duration * progressValue - CONSTANTS.SEEK_AMOUNT_SEC), 0, duration),
                );
                const [newSec, newMin] = newStringValue.split(':').reverse();

                AccessibilityInfo.announceForAccessibility(
                  t('seekToAnnouncement', { seconds: newSec, minutes: newMin }),
                );
              }
              break;
            case 'longpress':
              if (seekValue || seekValue === 0) {
                onControl(VideoControls.SEEK, seekValue);
              } else {
                onControl(VideoControls.TOGGLE_PAUSE);
              }
              break;
          }
        }}
        onBlur={() => setFocused(false)}
        onFocus={() => {
          setFocused(true);
        }}
        onPress={event => {
          showControls?.();
          if (seekValue || seekValue === 0) {
            onControl(VideoControls.SEEK, seekValue);
          } else {
            onControl(VideoControls.TOGGLE_PAUSE);
          }
        }}
      >
        <View style={styles.wrapper}>
          {hasData && (
            <Progress
              duration={duration}
              focused={focused}
              height={height}
              progress={makeInRange(progressValue, 0, 1)}
              width={width}
            />
          )}
          {isLive ? (
            <LiveIndicator />
          ) : (
            <TWCText noTestID importantForAccessibility={'no'} variant={'mediumText30'}>
              {TimeUtils.secondsToDuration(duration)}
            </TWCText>
          )}
        </View>
      </TWCAccessibleContent>
    );
  },
);

VideoProgressIndicator.propTypes = {
  progress: PropTypes.number,
  width: PropTypes.number,
  height: PropTypes.number,
  duration: PropTypes.number,
  isLive: PropTypes.bool,
  onControl: PropTypes.func,
  showControls: PropTypes.func,
  nextFocusLeft: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusRight: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusUp: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
  nextFocusDown: PropTypes.oneOfType([PropTypes.object, PropTypes.element, () => null]),
};

VideoProgressIndicator.defaultProps = {
  progress: null,
  width: null,
  height: null,
  duration: null,
  isLive: false,
  onControl: () => {},
  showControls: () => {},
  nextFocusLeft: null,
  nextFocusRight: null,
  nextFocusUp: null,
  nextFocusDown: null,
};

VideoProgressIndicator.displayName = 'VideoProgressIndicator';

const styles = StyleSheet.create({
  focusTimeString: {
    position: 'absolute',
    textAlign: 'center',
    top: -CONSTANTS.progressIndicatorHeight,
    width: CONSTANTS.progressTimeWidth,
  },
  liveDot: {
    backgroundColor: Theme.dodgerLightBlue,
    borderRadius: CONSTANTS.liveIndicatorDotSize / 2,
    height: CONSTANTS.liveIndicatorDotSize,
    marginRight: Theme.px(12),
    width: CONSTANTS.liveIndicatorDotSize,
  },
  progress: {
    alignItems: 'center',
    flexDirection: 'row',
    marginRight: Theme.px(27),
  },
  progressIndicator: {
    backgroundColor: Theme.whisperWhite,
    height: CONSTANTS.progressIndicatorHeight,
    width: CONSTANTS.progressIndicatorWidth,
  },
  progressLeft: {
    backgroundColor: Theme.whisperWhite,
    borderBottomLeftRadius: Theme.px(12),
    borderTopLeftRadius: Theme.px(12),
  },
  progressLeftFocus: {
    backgroundColor: Theme.dodgerLightBlue,
    borderBottomLeftRadius: Theme.px(12),
    borderTopLeftRadius: Theme.px(12),
  },
  progressRight: {
    backgroundColor: Theme.withOpacity(Theme.whisperWhite, 0.2),
    borderBottomRightRadius: Theme.px(12),
    borderTopRightRadius: Theme.px(12),
  },
  wrapper: {
    alignItems: 'center',
    flexDirection: 'row',
  },
});
